import type { Option } from 'baseui/select';

import { Select, SelectProps } from 'components/Select';
import { Input } from 'components/ui/input';
import { UtilityService } from 'services/api/utilities/endpoint';
import { Country } from 'services/api/utilities/types';
import { useTypedSelector } from 'store/store';

export function CountrySelect(
  props: Omit<SelectProps, 'value'> & {
    value?: string;
    useClientCountryQuery?: boolean;
    isCountryEmpty?: boolean;
    emptyPlaceholder?: string;
  },
) {
  const {
    value,
    useClientCountryQuery,
    isCountryEmpty,
    emptyPlaceholder,
    ...selectProps
  } = props;
  const clientId = useTypedSelector((s) => s.auth.userCredentials.clientId);

  const { data: countries = [] } = useClientCountryQuery
    ? UtilityService.endpoints.countryByClientId.useQuery(clientId)
    : UtilityService.endpoints.country.useQuery();

  const options: Option[] =
    Array.isArray(countries) && countries.length > 0
      ? countries.map((country: Country) => ({
          label: country.name,
          id: country.code,
        }))
      : [];

  const selected = options.filter((item) =>
    item.id ? value?.includes(item.id.toString()) : false,
  );

  // Some older records were saved before country fields were implemented.
  // If the selected country has no value, it means that the country is not available.
  // We show a disabled input "N/A" as a fallback instead of the Select component.
  // This is for the edit-customer-modal.tsx file.
  if (isCountryEmpty && options.length === 0) {
    const placeholderText = emptyPlaceholder || 'Select';
    const className =
      placeholderText === 'Select' ? 'text-gray-400' : 'opacity-50';
    return (
      <Input
        value=""
        placeholder={placeholderText}
        disabled={selectProps.disabled}
        className={className}
      />
    );
  }

  return (
    <Select
      {...selectProps}
      value={selected}
      options={options}
      creatable={false}
    />
  );
}
